import { useState } from 'react'
import type { ClassificacaoDeltaV } from '../../dominio/didatico/metricasCinematica'
import {
  avaliarLaudoInvestigador,
  type LaudoInvestigador,
  type RespostasInvestigador,
} from '../../dominio/didatico/modoInvestigador'
import type { PistaCena } from '../../dominio/didatico/pistasCena'
import type { TipoColisao } from '../../dominio/didatico/timelineCinematica'
import type { TrajetoriaFrontal } from '../../dominio/didatico/variacoesCinematica'
import { IconeConcluido, IconeConstrucao, IconeReproduzir } from '../icones'

interface Props {
  pistasDisponiveis: readonly PistaCena[]
  pistasInspecionadas: readonly string[]
  pistaSelecionada: PistaCena | null
  aoSelecionarPista: (id: string | null) => void
  cenarioReal: {
    tipoColisao: TipoColisao
    faixaDeltaV: ClassificacaoDeltaV
    deltaVKmh: number
    prioridadesCorretas: readonly ('X' | 'A' | 'B' | 'C' | 'D' | 'E')[]
    comCinto: boolean
    trajetoriaFrontal?: TrajetoriaFrontal
  }
  aoVerReplay: () => void
  aoExportarParaOficina: () => void
}

const OPCOES_COLISAO: readonly { id: TipoColisao; rotulo: string }[] = [
  { id: 'frontal', rotulo: 'Frontal' },
  { id: 'lateral', rotulo: 'Lateral' },
  { id: 'traseira', rotulo: 'Traseira' },
  { id: 'capotamento', rotulo: 'Capotamento' },
  { id: 'frontalComTraseira', rotulo: 'Engavetamento' },
]

const OPCOES_DELTAV: readonly { id: ClassificacaoDeltaV; rotulo: string }[] = [
  { id: 'baixo', rotulo: 'Baixo (< 20 km/h)' },
  { id: 'moderado', rotulo: 'Moderado (20-40 km/h)' },
  { id: 'grave', rotulo: 'Grave (> 40 km/h)' },
]

const LETRAS_XABCDE: readonly ('X' | 'A' | 'B' | 'C' | 'D' | 'E')[] = ['X', 'A', 'B', 'C', 'D', 'E']

/**
 * Painel interativo do Modo Investigador / CSI do Resgate (Fase 5).
 */
export function PainelInvestigadorCSI({
  pistasDisponiveis,
  pistasInspecionadas,
  pistaSelecionada,
  aoSelecionarPista,
  cenarioReal,
  aoVerReplay,
  aoExportarParaOficina,
}: Props) {
  const [direcao, setDirecao] = useState<TipoColisao | null>(null)
  const [faixaDeltaV, setFaixaDeltaV] = useState<ClassificacaoDeltaV | null>(null)
  const [prioridades, setPrioridades] = useState<readonly ('X' | 'A' | 'B' | 'C' | 'D' | 'E')[]>([])
  const [laudo, setLaudo] = useState<LaudoInvestigador | null>(null)

  const alternarPrioridade = (letra: 'X' | 'A' | 'B' | 'C' | 'D' | 'E') => {
    if (prioridades.includes(letra)) {
      setPrioridades(prioridades.filter((l) => l !== letra))
    } else {
      setPrioridades([...prioridades, letra])
    }
  }

  const submeterLaudo = () => {
    const respostas: RespostasInvestigador = {
      direcaoImpacto: direcao,
      faixaDeltaV,
      prioridadesXabcde: prioridades,
    }
    const resultado = avaliarLaudoInvestigador(respostas, cenarioReal)
    setLaudo(resultado)
  }

  const reiniciarLaudo = () => {
    setLaudo(null)
  }

  return (
    <div className="painel-investigador-csi">
      {/* Barra de status de investigação pericial */}
      <div className="progresso-investigacao">
        <span className="titulo-status-csi">Modo Investigador (CSI do Resgate)</span>
        <span className="contador-pistas">
          {pistasInspecionadas.length} de {pistasDisponiveis.length} pistas inspecionadas
        </span>
      </div>

      {/* Lista rápida de pistas / marcadores de cena */}
      <div className="grupo-controle">
        <span className="titulo-controle">Pistas de Cena & Evidências 3D</span>
        <div className="grade-pistas-chips">
          {pistasDisponiveis.map((p) => {
            const inspecionada = pistasInspecionadas.includes(p.id)
            const ativa = pistaSelecionada?.id === p.id
            return (
              <button
                key={p.id}
                type="button"
                className={`chip-pista ${inspecionada ? 'inspecionada' : ''} ${ativa ? 'ativo' : ''}`}
                onClick={() => aoSelecionarPista(ativa ? null : p.id)}
              >
                {inspecionada && <IconeConcluido />}
                {p.titulo}
              </button>
            )
          })}
        </div>
        <p className="dica">
          Toque nos marcadores luminosos no veículo 3D ou clique nas pistas acima para examinar
          os achados de trauma antes de emitir seu laudo.
        </p>
      </div>

      {/* Se o laudo ainda não foi emitido, mostra o formulário de dedução */}
      {!laudo ? (
        <div className="formulario-deducoes-csi">
          <div className="grupo-controle">
            <span className="titulo-controle">1. Qual foi a dinâmica principal do impacto?</span>
            <div className="grade-botoes-deducao">
              {OPCOES_COLISAO.map((op) => (
                <button
                  key={op.id}
                  type="button"
                  className={direcao === op.id ? 'ativo' : ''}
                  onClick={() => setDirecao(op.id)}
                >
                  {op.rotulo}
                </button>
              ))}
            </div>
          </div>

          <div className="grupo-controle">
            <span className="titulo-controle">2. Qual a estimativa de gravidade líquida (ΔV)?</span>
            <div className="grade-botoes-deducao">
              {OPCOES_DELTAV.map((op) => (
                <button
                  key={op.id}
                  type="button"
                  className={faixaDeltaV === op.id ? 'ativo' : ''}
                  onClick={() => setFaixaDeltaV(op.id)}
                >
                  {op.rotulo}
                </button>
              ))}
            </div>
          </div>

          <div className="grupo-controle">
            <span className="titulo-controle">3. Eixos prioritários no exame primário XABCDE:</span>
            <div className="grade-checkboxes-xabcde">
              {LETRAS_XABCDE.map((letra) => (
                <label key={letra} className="toggle-letra-csi">
                  <input
                    type="checkbox"
                    checked={prioridades.includes(letra)}
                    onChange={() => alternarPrioridade(letra)}
                  />
                  <span>{letra}</span>
                </label>
              ))}
            </div>
          </div>

          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--primario btn-submeter-laudo"
            onClick={submeterLaudo}
            disabled={!direcao || !faixaDeltaV}
          >
            Emitir Laudo Pericial
          </button>
        </div>
      ) : (
        /* Resultado do Laudo Pericial */
        <div className="resultado-laudo-pericial">
          <div className="cabecalho-resultado-laudo">
            <div className="pontuacao-destaque">
              <strong>{laudo.pontuacaoTotal}</strong>
              <span>/ 100 pontos</span>
            </div>
            <span className={`badge-desempenho desempenho-${laudo.classificacaoDesempenho.toLowerCase()}`}>
              {laudo.classificacaoDesempenho}
            </span>
          </div>

          <div className="itens-feedback-laudo">
            {laudo.itens.map((item, idx) => (
              <div key={idx} className={`item-feedback ${item.acertou ? 'item-correto' : 'item-incorreto'}`}>
                <div className="cabecalho-item-feedback">
                  <strong>{item.titulo}</strong>
                  <span className="pontos-item">+{item.pontos} pts</span>
                </div>
                <p className="texto-explicacao">{item.explicacao}</p>
                <div className="comparacao-respostas">
                  <span>Sua dedução: <em>{item.respostaUsuario}</em></span>
                  <span>Gabarito pericial: <strong>{item.gabarito}</strong></span>
                </div>
              </div>
            ))}
          </div>

          <div className="caixa-laudo-oficial">
            <pre>{laudo.laudoPericialOficial}</pre>
          </div>

          <div className="acoes-pos-laudo">
            <button
              type="button"
              className="btn-acao-cad btn-acao-cad--secundario"
              onClick={aoVerReplay}
              title="Executar simulação em velocidade lenta do início ao fim"
            >
              <IconeReproduzir /> Ver Replay da Física
            </button>
            <button
              type="button"
              className="btn-acao-cad btn-acao-cad--destaque"
              onClick={aoExportarParaOficina}
              title="Abrir a Oficina de Corte com os danos resultantes desta colisão"
            >
              <IconeConstrucao /> Desencarcerar este Veículo
            </button>
            <button
              type="button"
              className="btn-acao-cad btn-acao-cad--secundario"
              onClick={reiniciarLaudo}
            >
              Nova Investigação
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
